/// <reference types="vite/client" />
import i18next, { Resource } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const SUPPORTED_LANGUAGES = ["en", "es", "fr", "de", "zh"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  zh: "中文",
};

export const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  zh: "🇨🇳",
};

// Dynamically load all locale namespace files
function getResources() {
  const localeFiles = import.meta.glob("./locales/**/*.json", { eager: true });

  const resources: Resource = {};
  const namespaces = new Set<string>();

  for (const path in localeFiles) {
    // Extract language and namespace from path: ./locales/en/common.json
    const match = path.match(/\.\/locales\/([^/]+)\/(.+)\.json$/);
    if (match) {
      const language = match[1];
      const namespace = match[2];

      if (!language || !namespace) continue;

      if (!resources[language]) {
        resources[language] = {};
      }

      resources[language][namespace] = (localeFiles[path] as { default: Record<string, string> }).default;
      namespaces.add(namespace);
    }
  }

  return { resources, namespaces: Array.from(namespaces) };
}

// Initialize i18next
const initI18n = () => {
  const { resources, namespaces } = getResources();

  i18next.use(LanguageDetector).init({
    resources,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,
    defaultNS: "common",
    ns: namespaces,

    // Language detection options
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes
    },

    // React options
    react: {
      useSuspense: true,
    },

    // Use natural English strings as keys
    keySeparator: false, // Keys are natural text, no nested structure
    nsSeparator: false, // Namespace is passed via useTranslation(ns), not in the key
  });

  return i18next;
};

export const i18nInstance = initI18n();
export default i18nInstance;
